import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowRight, BookOpen, ClipboardList, Gamepad2, Loader2,
  Star, Trophy, Mic, MicOff, Headphones, Camera, Volume2, Video, ExternalLink, Upload, Calendar
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { getSignedFileUrl } from "@/lib/storage-utils";
import { motion, AnimatePresence } from "framer-motion";
import { useSpeech } from "@/hooks/useSpeech";
import LessonPlayer from "@/components/parent/LessonPlayer";
import LessonSlidesViewer from "@/components/lesson/LessonSlidesViewer";
import ActivityPlayer from "@/components/activities/ActivityPlayer";
import BehavioralActivity from "@/components/activities-page/BehavioralActivity";
import HealthActivity from "@/components/activities-page/HealthActivity";
import CreativeActivity from "@/components/activities-page/CreativeActivity";
import SocialActivity from "@/components/activities-page/SocialActivity";
import ActivitySummaryPanel from "@/components/activities-page/ActivitySummaryPanel";
import AchievementBadges from "@/components/activities-page/AchievementBadges";
import ActivityHistory from "@/components/activities-page/ActivityHistory";
import AIActivitySuggestions from "@/components/activities-page/AIActivitySuggestions";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Heart, Palette, Users } from "lucide-react";

// Extend window for webkitSpeechRecognition
declare global {
  interface Window {
    webkitSpeechRecognition: any;
    SpeechRecognition: any;
  }
}

const DAYS = ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];
const DAY_COLORS_STUDENT = [
  "bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800",
  "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800",
  "bg-purple-50 border-purple-200 dark:bg-purple-950/30 dark:border-purple-800",
  "bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800",
  "bg-rose-50 border-rose-200 dark:bg-rose-950/30 dark:border-rose-800",
  "bg-gray-50 border-gray-200 dark:bg-gray-950/30 dark:border-gray-800",
  "bg-cyan-50 border-cyan-200 dark:bg-cyan-950/30 dark:border-cyan-800",
];

function TodaySchedule({ classId }: { classId: string | null }) {
  const todayIdx = new Date().getDay();
  const { data: todaySlots, isLoading } = useQuery({
    queryKey: ["student-today-schedule", classId, todayIdx],
    queryFn: async () => {
      if (!classId) return [];
      const { data } = await supabase
        .from("class_schedules")
        .select("*, staff(full_name)")
        .eq("class_id", classId)
        .eq("day_of_week", todayIdx)
        .order("start_time");
      return data || [];
    },
    enabled: !!classId,
  });

  if (!classId || isLoading || !todaySlots?.length) return null;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Card className="border-primary/20">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-5 h-5 text-primary" />
            <h3 className="font-bold text-foreground text-sm">جدول اليوم - {DAYS[todayIdx]}</h3>
          </div>
          <div className="space-y-2">
            {todaySlots.map((slot: any) => (
              <div key={slot.id} className={`p-3 rounded-lg border ${DAY_COLORS_STUDENT[todayIdx]} flex items-center gap-3`}>
                <div className="text-center min-w-[60px]">
                  <p className="text-xs font-bold text-foreground">{slot.start_time?.slice(0, 5)}</p>
                  <p className="text-[10px] text-muted-foreground">{slot.end_time?.slice(0, 5)}</p>
                </div>
                <div className="h-8 w-px bg-border" />
                <div className="flex-1">
                  <p className="font-medium text-sm text-foreground">{slot.subject}</p>
                  {(slot as any).staff?.full_name && (
                    <p className="text-xs text-muted-foreground">{(slot as any).staff.full_name}</p>
                  )}
                  {slot.notes && <p className="text-xs text-muted-foreground italic mt-0.5">📝 {slot.notes}</p>}
                </div>
                {slot.room && <Badge variant="outline" className="text-[10px]">📍 {slot.room}</Badge>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

const encouragements = [
  "أحسنت يا بطل! 🎉",
  "ممتاز! استمر 💪",
  "رائع جداً! 🌟",
  "أنت نجم! ⭐",
  "عمل رائع! 🏆",
];

const StudentDashboard = () => {
  const { childId } = useParams<{ childId: string }>();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const nurseryId = profile?.nursery_id;
  const { speak, stopSpeaking, isSpeaking } = useSpeech();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [voicesLoaded, setVoicesLoaded] = useState(false);

  // Assignment submission state
  const [submittingAssignment, setSubmittingAssignment] = useState<any>(null);
  const [submitFile, setSubmitFile] = useState<File | null>(null);
  const [submitFilePreview, setSubmitFilePreview] = useState<string | null>(null);
  const [submitTextAnswer, setSubmitTextAnswer] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);

  // Ensure voices are loaded before first speak call
  useEffect(() => {
    const synth = window.speechSynthesis;
    const loadVoices = () => {
      if (synth.getVoices().length > 0) setVoicesLoaded(true);
    };
    loadVoices();
    synth.addEventListener("voiceschanged", loadVoices);
    return () => synth.removeEventListener("voiceschanged", loadVoices);
  }, []);

  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [selectedLesson, setSelectedLesson] = useState<any>(null);
  const [lessonFiles, setLessonFiles] = useState<any[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [showEncouragement, setShowEncouragement] = useState(false);
  const [encourageMsg, setEncourageMsg] = useState("");
  const [showSmartNotebook, setShowSmartNotebook] = useState(false);

  // Activities state
  const [activityView, setActivityView] = useState<string>("menu");
  const [activityTab, setActivityTab] = useState("activities");
  const [activityStats, setActivityStats] = useState({ totalStars: 0, totalScore: 0, completedToday: 0, totalActivities: 0, streak: 0 });

  const fetchActivityStats = useCallback(async () => {
    if (!childId) return;
    const today = new Date().toISOString().split("T")[0];
    const { data: allData } = await supabase
      .from("child_activities" as any)
      .select("score, stars, completed_at")
      .eq("child_id", childId)
      .order("completed_at", { ascending: false });
    const items = (allData || []) as any[];
    const todayItems = items.filter((a: any) => a.completed_at?.startsWith(today));
    let streak = 0;
    const dates = [...new Set(items.map((a: any) => a.completed_at?.split("T")[0]).filter(Boolean))].sort().reverse();
    const d = new Date();
    for (const date of dates) {
      if (date === d.toISOString().split("T")[0]) { streak++; d.setDate(d.getDate() - 1); } else break;
    }
    setActivityStats({
      totalStars: items.reduce((s: number, a: any) => s + (a.stars || 0), 0),
      totalScore: items.reduce((s: number, a: any) => s + (a.score || 0), 0),
      completedToday: todayItems.length,
      totalActivities: items.length,
      streak,
    });
  }, [childId]);

  useEffect(() => { if (childId && activeSection === "activities") fetchActivityStats(); }, [childId, activeSection]);

  const handleActivityComplete = async (score: number, stars: number, activityKey: string) => {
    if (!childId || !effectiveNurseryId) return;
    const activityType = activityKey.startsWith("behavioral") ? "behavioral" : activityKey.startsWith("health") ? "health" : activityKey.startsWith("social") ? "social" : "creative";
    const activityNames: Record<string, string> = {
      behavioral_scenarios: "سيناريوهات سلوكية", health_challenges: "تحديات حركية",
      creative_story: "إكمال قصة", creative_colors: "عالم الألوان", creative_feelings: "ارسم مشاعرك",
      creative_coloring: "التلوين التفاعلي", creative_matching: "مطابقة الصور", creative_puzzle: "تركيب الأشكال",
      social_scenarios: "أنشطة اجتماعية",
    };
    try {
      await supabase.from("child_activities" as any).insert({
        nursery_id: nurseryId, child_id: childId, activity_type: activityType,
        activity_key: activityKey, activity_name: activityNames[activityKey] || activityKey, score, stars,
      } as any);
      fetchActivityStats();
      triggerEncouragement();
    } catch (e) { console.error(e); }
  };

  // Fetch child
  const { data: child, isLoading: loadingChild } = useQuery({
    queryKey: ["student-child", childId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("children")
        .select("*, classes(name)")
        .eq("id", childId!)
        .eq("parent_id", user!.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!childId && !!user,
  });

  // Use child's nursery_id as fallback when parent profile nursery_id is missing
  const effectiveNurseryId = nurseryId || child?.nursery_id;

  // Fetch lessons - show ALL nursery lessons so the student can access all content
  const { data: lessons } = useQuery({
    queryKey: ["student-lessons", effectiveNurseryId],
    queryFn: async () => {
      const { data } = await supabase
        .from("lessons")
        .select("*, classes(name)")
        .eq("nursery_id", effectiveNurseryId!)
        .order("created_at", { ascending: false })
        .limit(50);
      return data || [];
    },
    enabled: !!effectiveNurseryId && !!child,
  });

  // Fetch assignments - show all nursery assignments
  const { data: assignments } = useQuery({
    queryKey: ["student-assignments", effectiveNurseryId],
    queryFn: async () => {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const { data } = await supabase
        .from("assignments")
        .select("*, assignment_submissions(status, child_id)")
        .eq("nursery_id", effectiveNurseryId!)
        .gte("due_date", thirtyDaysAgo.toISOString())
        .order("due_date", { ascending: false });
      
      return data || [];
    },
    enabled: !!effectiveNurseryId && !!child,
  });

  // Fetch exams
  const { data: exams } = useQuery({
    queryKey: ["student-exams", effectiveNurseryId],
    queryFn: async () => {
      const { data } = await supabase
        .from("exams")
        .select("*")
        .eq("nursery_id", effectiveNurseryId!)
        .eq("is_interactive", true)
        .order("scheduled_at", { ascending: false })
        .limit(10);
      return data || [];
    },
    enabled: !!effectiveNurseryId && !!child,
  });

  // Fetch live sessions
  const { data: liveSessions } = useQuery({
    queryKey: ["student-live-sessions", effectiveNurseryId],
    queryFn: async () => {
      const { data } = await supabase
        .from("live_sessions")
        .select("*")
        .eq("nursery_id", effectiveNurseryId!)
        .in("status", ["scheduled", "live"])
        .order("scheduled_at")
        .limit(10);
      return data || [];
    },
    enabled: !!effectiveNurseryId && !!child,
  });

  // Fetch lesson explanation for selected lesson (any status)
  const { data: lessonExplanation, refetch: refetchExplanation } = useQuery({
    queryKey: ["student-lesson-explanation", selectedLesson?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("lesson_explanations")
        .select("*")
        .eq("lesson_id", selectedLesson!.id)
        .maybeSingle();
      return data;
    },
    enabled: !!selectedLesson?.id,
  });

  const [generatingExplanation, setGeneratingExplanation] = useState(false);
  const [generatingImages, setGeneratingImages] = useState<Set<number>>(new Set());

  const handleGenerateExplanation = async () => {
    if (!selectedLesson || !effectiveNurseryId) return;
    setGeneratingExplanation(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-lesson-explanation", {
        body: {
          lessonId: selectedLesson.id,
          lessonTitle: selectedLesson.title,
          lessonContent: selectedLesson.content,
          effectiveNurseryId,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast({ title: "تم إنشاء الشرح بنجاح! 🎉" });
      await refetchExplanation();
      setShowSmartNotebook(true);
      triggerEncouragement();
    } catch (err: any) {
      toast({ title: "خطأ", description: err.message || "فشل في توليد الشرح", variant: "destructive" });
    } finally {
      setGeneratingExplanation(false);
    }
  };

  const handleGenerateSlideImage = async (slideIndex: number, imagePrompt: string) => {
    if (!lessonExplanation) return;
    setGeneratingImages(prev => new Set([...prev, slideIndex]));
    try {
      const { data, error } = await supabase.functions.invoke("generate-slide-image", {
        body: { explanationId: lessonExplanation.id, slideIndex, imagePrompt, effectiveNurseryId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      await refetchExplanation();
    } catch (err: any) {
      toast({ title: "خطأ في توليد الصورة", variant: "destructive" });
    } finally {
      setGeneratingImages(prev => { const n = new Set(prev); n.delete(slideIndex); return n; });
    }
  };

  // Fetch game results
  const { data: gameResults } = useQuery({
    queryKey: ["student-games", childId],
    queryFn: async () => {
      const { data } = await supabase
        .from("game_results")
        .select("*")
        .eq("child_id", childId!)
        .order("played_at", { ascending: false })
        .limit(20);
      return data || [];
    },
    enabled: !!childId,
  });

  const triggerEncouragement = () => {
    const msg = encouragements[Math.floor(Math.random() * encouragements.length)];
    setEncourageMsg(msg);
    setShowEncouragement(true);
    speak(msg.replace(/[🎉💪🌟⭐🏆]/g, ""));
    setTimeout(() => setShowEncouragement(false), 3000);
  };

  // Voice recognition for assignments
  const startVoiceAnswer = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast({ title: "⚠️ متصفحك لا يدعم التعرف على الكلام", variant: "destructive" });
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = "ar-SA";
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setSubmitTextAnswer(prev => prev ? prev + " " + transcript : transcript);
      setIsListening(false);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  };

  const stopVoiceAnswer = () => {
    if (recognitionRef.current) try { recognitionRef.current.stop(); } catch {}
    setIsListening(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setSubmitFile(f);
    if (f.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (ev) => setSubmitFilePreview(ev.target?.result as string);
      reader.readAsDataURL(f);
    } else {
      setSubmitFilePreview(null);
    }
  };

  const handleSubmitAssignment = async () => {
    if (!submittingAssignment || !childId || !user || !effectiveNurseryId) return;
    if (!submitFile && !submitTextAnswer.trim()) {
      toast({ title: "يرجى رفع صورة أو كتابة/تسجيل إجابة", variant: "destructive" });
      return;
    }
    setSubmitLoading(true);
    let fileUrl: string | null = null;
    if (submitFile) {
      const ext = submitFile.name.split(".").pop();
      const path = `${effectiveNurseryId}/submissions/${childId}/${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage.from("homework-files").upload(path, submitFile);
      if (uploadError) {
        toast({ title: "خطأ في رفع الملف", description: uploadError.message, variant: "destructive" });
        setSubmitLoading(false);
        return;
      }
      fileUrl = path;
    }
    const { error } = await supabase.from("assignment_submissions").insert({
      assignment_id: submittingAssignment.id,
      child_id: childId,
      submitted_by: user.id,
      file_url: fileUrl,
      text_answer: submitTextAnswer.trim() || null,
      status: "submitted",
      submitted_at: new Date().toISOString(),
    });
    if (error) {
      toast({ title: "خطأ", description: error.message, variant: "destructive" });
    } else {
      setSubmittingAssignment(null);
      setSubmitTextAnswer("");
      setSubmitFile(null);
      setSubmitFilePreview(null);
      triggerEncouragement();
      qc.invalidateQueries({ queryKey: ["student-assignments"] });
    }
    setSubmitLoading(false);
  };

  const handleSelectLesson = async (lesson: any) => {
    setSelectedLesson(lesson);
    setLoadingFiles(true);
    const { data: files } = await supabase
      .from("lesson_files")
      .select("*")
      .eq("lesson_id", lesson.id)
      .order("created_at");
    const filesWithUrls = await Promise.all(
      (files || []).map(async (f: any) => {
        const signedUrl = await getSignedFileUrl("lesson-files", f.file_url);
        return { ...f, signed_url: signedUrl || f.file_url };
      })
    );
    setLessonFiles(filesWithUrls);
    setLoadingFiles(false);
  };

  const pendingAssignments = (assignments || []).filter((a: any) => {
    const subs = a.assignment_submissions || [];
    return !subs.some((s: any) => s.child_id === childId && s.status !== "pending");
  });

  if (loadingChild) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-amber-500" />
      </div>
    );
  }

  if (!child) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">لم يتم العثور على بيانات الطالب</p>
        <Button variant="ghost" onClick={() => navigate("/dashboard/parent")} className="mt-4">العودة</Button>
      </div>
    );
  }

  const activeLiveSessions = (liveSessions || []).filter((s: any) => s.status === "live");
  const upcomingLiveSessions = (liveSessions || []).filter((s: any) => s.status === "scheduled");

  const menuItems = [
    { id: "lessons", label: "الدروس", icon: "📚", color: "from-blue-400 to-blue-600", count: lessons?.length || 0 },
    { id: "assignments", label: "الواجبات", icon: "📝", color: "from-green-400 to-green-600", count: pendingAssignments.length },
    { id: "exams", label: "الامتحانات", icon: "🎤", color: "from-purple-400 to-purple-600", count: exams?.length || 0 },
    { id: "live", label: "البث المباشر", icon: "📺", color: "from-red-400 to-red-600", count: (liveSessions || []).length, live: activeLiveSessions.length > 0 },
    { id: "games", label: "الألعاب", icon: "🎮", color: "from-amber-400 to-amber-600", count: gameResults?.length || 0 },
    { id: "activities", label: "الأنشطة التكميلية", icon: "🌈", color: "from-pink-400 to-rose-600", count: 0 },
    { id: "phonics", label: "الفونكس 🔤", icon: "🔊", color: "from-blue-500 to-indigo-700", count: 0 },
    { id: "mentalmath", label: "الحساب الذهني 🧮", icon: "🧮", color: "from-rose-500 to-red-700", count: 0 },
  ];

  return (
    <div className="space-y-6 relative">
      {/* Encouragement overlay */}
      <AnimatePresence>
        {showEncouragement && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
            onClick={() => setShowEncouragement(false)}
          >
            <motion.div
              animate={{ rotate: [0, -5, 5, -5, 0] }}
              transition={{ duration: 0.5 }}
              className="bg-gradient-to-br from-amber-300 to-amber-500 rounded-3xl p-10 shadow-2xl text-center"
            >
              <p className="text-4xl mb-3">🌟</p>
              <p className="text-2xl font-bold text-white">{encourageMsg}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard/parent")}>
          <ArrowRight className="w-5 h-5" />
        </Button>
        <Avatar className="w-14 h-14 ring-4 ring-amber-300">
          <AvatarImage src={child.photo_url || ""} />
          <AvatarFallback className="bg-amber-100 text-amber-700 font-bold text-xl">
            {child.full_name?.charAt(0)}
          </AvatarFallback>
        </Avatar>
        <div>
          <h1 className="text-2xl font-bold text-foreground">أهلاً {child.full_name?.split(" ")[0]}! 🎉</h1>
          {child.classes && (
            <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-xs mt-1">
              {child.classes.name}
            </Badge>
          )}
        </div>
      </div>

      {/* Stars summary */}
      {gameResults && gameResults.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="bg-gradient-to-l from-amber-50 to-amber-100/50 dark:from-amber-950/20 dark:to-amber-900/10 border-amber-200">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, Math.round((gameResults || []).reduce((s: number, g: any) => s + (g.stars || 0), 0) / Math.max(gameResults.length, 1))) }).map((_, i) => (
                  <Star key={i} className="w-6 h-6 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <div className="flex-1">
                <p className="font-bold text-foreground text-sm">
                  مجموع النجوم: {(gameResults || []).reduce((s: number, g: any) => s + (g.stars || 0), 0)} ⭐
                </p>
                <p className="text-xs text-muted-foreground">{gameResults.length} لعبة تم لعبها</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-amber-300 text-amber-700"
                onClick={triggerEncouragement}
              >
                🎉 تشجيع
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Today's Schedule */}
      {!activeSection && <TodaySchedule classId={child.class_id} />}

      {/* Main Menu or Active Section */}
      {!activeSection ? (
        <div className="grid grid-cols-2 gap-4">
          {menuItems.map((item, i) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.1 }}
            >
              <Card
                className={`cursor-pointer hover:shadow-xl transition-all hover:scale-105 overflow-hidden`}
                onClick={() => (item.id === "phonics" || item.id === "mentalmath") ? navigate("/dashboard/interactive-learning") : setActiveSection(item.id)}
              >
                <CardContent className={`p-6 text-center bg-gradient-to-br ${item.color} text-white relative`}>
                  {(item as any).live && (
                    <div className="absolute top-2 left-2 flex items-center gap-1 bg-white/30 rounded-full px-2 py-0.5">
                      <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                      <span className="text-[10px] font-bold">LIVE</span>
                    </div>
                  )}
                  <p className="text-5xl mb-3">{item.icon}</p>
                  <h3 className="text-lg font-bold">{item.label}</h3>
                  {item.count > 0 && (
                    <Badge className="bg-white/30 text-white border-white/30 mt-2 text-sm">
                      {item.count}
                    </Badge>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          <Button
            variant="outline"
            onClick={() => { setActiveSection(null); setSelectedLesson(null); }}
            className="gap-2"
          >
            <ArrowRight className="w-4 h-4" />
            العودة للقائمة الرئيسية
          </Button>

          {/* === LESSONS === */}
          {activeSection === "lessons" && (
            <div className="space-y-3">
              <h2 className="text-xl font-bold text-foreground flex items-center gap-2">📚 الدروس</h2>
              {selectedLesson ? (
                <Card className="shadow-card">
                  <CardContent className="p-5 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-bold text-foreground">{selectedLesson.title}</h3>
                      <Button size="sm" variant="ghost" onClick={() => setSelectedLesson(null)}>✕</Button>
                    </div>
                    {selectedLesson.content && (
                      <div className="bg-blue-50 dark:bg-blue-950/20 rounded-xl p-4">
                        <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{selectedLesson.content}</p>
                      </div>
                    )}
                    {isSpeaking ? (
                      <Button
                        className="w-full gap-2 bg-red-500 hover:bg-red-600 text-white text-lg py-6 rounded-xl animate-pulse"
                        onClick={stopSpeaking}
                      >
                        <Volume2 className="w-6 h-6" />
                        ⏹️ إيقاف الاستماع
                      </Button>
                    ) : (
                      <Button
                        className="w-full gap-2 bg-blue-500 hover:bg-blue-600 text-white text-lg py-6 rounded-xl"
                        onClick={() => {
                          if (selectedLesson.content) {
                            speak(selectedLesson.content);
                          }
                        }}
                        disabled={!selectedLesson.content}
                      >
                        <Headphones className="w-6 h-6" />
                        🎧 استمع للدرس
                      </Button>
                    )}
                    {!selectedLesson.content && (
                      <p className="text-xs text-muted-foreground text-center">لا يوجد محتوى نصي لهذا الدرس</p>
                    )}

                    {/* AI Smart Notebook Button - Always visible */}
                    {lessonExplanation?.status === "ready" && (lessonExplanation.slides as any[])?.length > 0 ? (
                      <Button
                        className="w-full gap-2 bg-gradient-to-l from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white text-lg py-6 rounded-xl shadow-lg"
                        onClick={() => setShowSmartNotebook(true)}
                      >
                        <BookOpen className="w-6 h-6" />
                        📖 الكراسة الذكية (شرح بالذكاء الاصطناعي)
                      </Button>
                    ) : (
                      <Button
                        className="w-full gap-2 bg-gradient-to-l from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white text-lg py-6 rounded-xl shadow-lg"
                        onClick={handleGenerateExplanation}
                        disabled={generatingExplanation || lessonExplanation?.status === "generating"}
                      >
                        {generatingExplanation || lessonExplanation?.status === "generating" ? (
                          <>
                            <Loader2 className="w-6 h-6 animate-spin" />
                            ✨ جاري إنشاء الكراسة الذكية...
                          </>
                        ) : (
                          <>
                            <BookOpen className="w-6 h-6" />
                            ✨ أنشئ الكراسة الذكية بالذكاء الاصطناعي
                          </>
                        )}
                      </Button>
                    )}

                    {loadingFiles ? (
                      <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin" /></div>
                    ) : lessonFiles.length > 0 && (
                      <div className="space-y-3">
                        <h4 className="font-semibold text-sm">📎 الملفات</h4>
                        {lessonFiles.map((f: any) => {
                          const isPdf = f.file_name?.toLowerCase().endsWith('.pdf') || f.file_type === 'application/pdf';
                          const isImage = f.file_type?.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(f.file_name);
                          return (
                            <div key={f.id} className="space-y-2">
                              <div className="flex items-center gap-2 p-2 rounded-lg border bg-muted/20">
                                <span className="text-lg">{isPdf ? '📄' : isImage ? '🖼️' : '📎'}</span>
                                <span className="text-sm font-medium flex-1 truncate">{f.file_name}</span>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-xs gap-1"
                                  onClick={() => window.open(f.signed_url, '_blank')}
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  فتح
                                </Button>
                              </div>
                              {isPdf && (
                                <div className="space-y-2">
                                  <iframe
                                    src={`${f.signed_url}#view=FitH`}
                                    className="w-full rounded-lg border bg-white"
                                    style={{ height: '500px' }}
                                    title={f.file_name}
                                    loading="lazy"
                                  />
                                  <p className="text-xs text-muted-foreground">
                                    إذا لم يظهر الملف، استخدم زر «فتح» بالأعلى.
                                  </p>
                                </div>
                              )}
                              {isImage && (
                                <img
                                  src={f.signed_url}
                                  alt={f.file_name}
                                  className="w-full rounded-lg border max-h-[400px] object-contain bg-muted/10"
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Interactive Activities */}
                    {effectiveNurseryId && childId && (
                      <div className="pt-4 border-t">
                        <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                          <Gamepad2 className="w-4 h-4 text-primary" />
                          🎮 الأنشطة التفاعلية
                        </h4>
                        <ActivityPlayer
                          lessonId={selectedLesson.id}
                          childId={childId}
                          nurseryId={effectiveNurseryId!}
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-3">
                  {(lessons || []).map((l: any, i: number) => (
                    <motion.div key={l.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}>
                      <Card
                        className="cursor-pointer hover:shadow-md transition-all hover:scale-[1.01] border-r-4 border-r-blue-400"
                        onClick={() => handleSelectLesson(l)}
                      >
                        <CardContent className="p-4 flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-950/30 flex items-center justify-center text-2xl shrink-0">📖</div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-foreground truncate">{l.title}</p>
                            {l.scheduled_at && (
                              <p className="text-xs text-muted-foreground">{format(new Date(l.scheduled_at), "dd MMM", { locale: ar })}</p>
                            )}
                          </div>
                          <Headphones className="w-5 h-5 text-blue-400" />
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                  {(!lessons || lessons.length === 0) && (
                    <div className="text-center py-12 text-muted-foreground">لا توجد دروس حالياً</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* === ASSIGNMENTS === */}
          {activeSection === "assignments" && (
            <div className="space-y-3">
              <h2 className="text-xl font-bold text-foreground flex items-center gap-2">📝 الواجبات</h2>
              {pendingAssignments.length === 0 ? (
                <Card className="shadow-card">
                  <CardContent className="py-12 text-center">
                    <p className="text-5xl mb-3">🎉</p>
                    <p className="text-lg font-bold text-foreground">لا توجد واجبات معلقة!</p>
                    <p className="text-sm text-muted-foreground">أحسنت يا بطل</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-3">
                  {pendingAssignments.map((a: any, i: number) => (
                    <motion.div key={a.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                      <Card className="border-r-4 border-r-green-400 hover:shadow-md transition-all">
                        <CardContent className="p-4 space-y-3">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-950/30 flex items-center justify-center text-2xl shrink-0">📒</div>
                            <div className="flex-1">
                              <p className="font-bold text-foreground">{a.title}</p>
                              <p className="text-xs text-muted-foreground">
                                آخر موعد: {format(new Date(a.due_date), "dd MMM", { locale: ar })}
                              </p>
                            </div>
                          </div>
                          {a.description && (
                            <p className="text-sm text-muted-foreground bg-green-50 dark:bg-green-950/20 rounded-lg p-3">{a.description}</p>
                          )}
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              className="flex-1 gap-1 bg-green-500 hover:bg-green-600 text-white rounded-xl"
                              onClick={() => setSubmittingAssignment(a)}
                            >
                              <Camera className="w-4 h-4" />
                              📷 رفع صورة
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1 gap-1 border-green-300 text-green-700 rounded-xl"
                              onClick={() => {
                                setSubmittingAssignment(a);
                                setTimeout(() => startVoiceAnswer(), 500);
                              }}
                            >
                              <Mic className="w-4 h-4" />
                              🎤 تسجيل صوتي
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* === EXAMS === */}
          {activeSection === "exams" && (
            <div className="space-y-3">
              <h2 className="text-xl font-bold text-foreground flex items-center gap-2">🎤 الامتحانات</h2>
              {(!exams || exams.length === 0) ? (
                <Card className="shadow-card">
                  <CardContent className="py-12 text-center">
                    <p className="text-5xl mb-3">📋</p>
                    <p className="text-lg font-bold text-foreground">لا توجد امتحانات حالياً</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-3">
                  {exams.map((e: any, i: number) => (
                    <motion.div key={e.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                      <Card
                        className="border-r-4 border-r-purple-400 hover:shadow-md transition-all cursor-pointer"
                        onClick={() => navigate(`/dashboard/exam/${e.id}/take`)}
                      >
                        <CardContent className="p-4 flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-950/30 flex items-center justify-center text-2xl shrink-0">📝</div>
                          <div className="flex-1">
                            <p className="font-bold text-foreground">{e.title}</p>
                            {e.scheduled_at && (
                              <p className="text-xs text-muted-foreground">
                                {format(new Date(e.scheduled_at), "dd MMM yyyy", { locale: ar })}
                              </p>
                            )}
                            {e.duration_minutes && (
                              <Badge variant="secondary" className="text-xs mt-1">{e.duration_minutes} دقيقة</Badge>
                            )}
                          </div>
                          <div className="flex flex-col items-center gap-1">
                            <Mic className="w-5 h-5 text-purple-400" />
                            <span className="text-[10px] text-muted-foreground">جاوب بالصوت</span>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* === LIVE SESSIONS === */}
          {activeSection === "live" && (
            <div className="space-y-3">
              <h2 className="text-xl font-bold text-foreground flex items-center gap-2">📺 البث المباشر</h2>

              {/* Active live sessions */}
              {activeLiveSessions.length > 0 && (
                <div className="space-y-3">
                  <Badge className="bg-red-500 text-white animate-pulse text-sm px-3 py-1">🔴 جلسات مباشرة الآن</Badge>
                  {activeLiveSessions.map((s: any, i: number) => (
                    <motion.div key={s.id} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.1 }}>
                      <Card className="border-2 border-red-400 shadow-lg overflow-hidden">
                        <CardContent className="p-0">
                          <div className="bg-gradient-to-l from-red-500 to-red-600 p-4 text-white">
                            <div className="flex items-center gap-3">
                              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center text-3xl">📺</div>
                              <div className="flex-1">
                                <h3 className="font-bold text-lg">{s.title}</h3>
                                {s.description && <p className="text-sm text-red-100">{s.description}</p>}
                                <div className="flex items-center gap-2 mt-1">
                                  <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                                  <span className="text-xs">مباشر الآن</span>
                                  {s.duration_minutes && <span className="text-xs">• {s.duration_minutes} دقيقة</span>}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="p-4">
                            <Button
                              className="w-full gap-2 bg-red-500 hover:bg-red-600 text-white text-lg py-6 rounded-xl"
                              onClick={() => window.open(s.meeting_url, "_blank")}
                            >
                              <Video className="w-6 h-6" />
                              🚀 انضم للبث الآن!
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Upcoming sessions */}
              {upcomingLiveSessions.length > 0 && (
                <div className="space-y-3">
                  {activeLiveSessions.length > 0 && (
                    <h3 className="font-semibold text-foreground text-sm mt-4">📅 جلسات قادمة</h3>
                  )}
                  {upcomingLiveSessions.map((s: any, i: number) => (
                    <motion.div key={s.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                      <Card className="border-r-4 border-r-red-300 hover:shadow-md transition-all">
                        <CardContent className="p-4 flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-950/30 flex items-center justify-center text-2xl shrink-0">📅</div>
                          <div className="flex-1">
                            <p className="font-bold text-foreground">{s.title}</p>
                            {s.scheduled_at && (
                              <p className="text-xs text-muted-foreground">
                                {format(new Date(s.scheduled_at), "EEEE dd MMM - HH:mm", { locale: ar })}
                              </p>
                            )}
                            {s.duration_minutes && (
                              <Badge variant="secondary" className="text-xs mt-1">{s.duration_minutes} دقيقة</Badge>
                            )}
                          </div>
                          <Badge variant="outline" className="text-xs shrink-0 gap-1">
                            <Video className="w-3 h-3" />
                            {s.meeting_platform}
                          </Badge>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )}

              {(liveSessions || []).length === 0 && (
                <Card className="shadow-card">
                  <CardContent className="py-12 text-center">
                    <p className="text-5xl mb-3">📺</p>
                    <p className="text-lg font-bold text-foreground">لا توجد جلسات بث مباشر حالياً</p>
                    <p className="text-sm text-muted-foreground">ترقب الجلسات القادمة!</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* === GAMES === */}
          {activeSection === "games" && (
            <div className="space-y-3">
              <h2 className="text-xl font-bold text-foreground flex items-center gap-2">🎮 إنجازاتي</h2>
              <div className="flex gap-2 flex-wrap mb-3">
                <Button
                  onClick={() => navigate(`/dashboard/student/${childId}/cartoon-games`)}
                  className="gap-2 bg-gradient-to-l from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white rounded-xl text-base py-5 flex-1"
                >
                  🎨 ألعاب كرتونية
                </Button>
                <Button
                  onClick={() => navigate("/dashboard/interactive-learning")}
                  className="gap-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl flex-1"
                >
                  <Gamepad2 className="w-5 h-5" />
                  🎮 ألعاب تعليمية
                </Button>
              </div>
              {(!gameResults || gameResults.length === 0) ? (
                <Card className="shadow-card">
                  <CardContent className="py-12 text-center">
                    <p className="text-5xl mb-3">🎮</p>
                    <p className="text-lg font-bold text-foreground">لم تلعب أي لعبة بعد</p>
                    <p className="text-sm text-muted-foreground">ابدأ اللعب واجمع النجوم!</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-3">
                  {gameResults.map((g: any, i: number) => (
                    <motion.div key={g.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}>
                      <Card className="border-r-4 border-r-amber-400">
                        <CardContent className="p-3 flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-950/30 flex items-center justify-center text-xl shrink-0">🏆</div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-foreground text-sm truncate">{g.game_type}</p>
                            <p className="text-xs text-muted-foreground">
                              {format(new Date(g.played_at), "dd MMM", { locale: ar })}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {Array.from({ length: g.stars || 0 }).map((_, j) => (
                              <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />
                            ))}
                          </div>
                          <Badge variant="outline" className="text-xs shrink-0">
                            {g.score}/{g.max_score}
                          </Badge>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* === ACTIVITIES === */}
          {activeSection === "activities" && (
            <div className="space-y-4">
              {activityView !== "menu" ? (
                <div className="max-w-2xl mx-auto animate-fade-in">
                  {activityView === "behavioral" && (
                    <BehavioralActivity onComplete={handleActivityComplete} onBack={() => setActivityView("menu")} />
                  )}
                  {activityView === "health" && (
                    <HealthActivity onComplete={handleActivityComplete} onBack={() => setActivityView("menu")} />
                  )}
                  {activityView === "creative" && (
                    <CreativeActivity onComplete={handleActivityComplete} onBack={() => setActivityView("menu")} />
                  )}
                  {activityView === "social" && (
                    <SocialActivity onComplete={handleActivityComplete} onBack={() => setActivityView("menu")} />
                  )}
                  {activityView === "ai" && (
                    <div className="space-y-4">
                      <Button onClick={() => setActivityView("menu")} variant="ghost" size="sm">
                        <ArrowLeft className="h-4 w-4 ml-1" /> رجوع
                      </Button>
                      <AIActivitySuggestions childId={childId!} childName={child?.full_name || ""} />
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4" dir="rtl">
                  <h2 className="text-xl font-bold text-foreground flex items-center gap-2">🌈 الأنشطة التكميلية</h2>

                  <ActivitySummaryPanel {...activityStats} />

                  <Tabs value={activityTab} onValueChange={setActivityTab}>
                    <TabsList className="grid w-full grid-cols-3">
                      <TabsTrigger value="activities">🎮 الأنشطة</TabsTrigger>
                      <TabsTrigger value="badges">🏆 الشارات</TabsTrigger>
                      <TabsTrigger value="history">📋 السجل</TabsTrigger>
                    </TabsList>

                    <TabsContent value="activities" className="mt-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {[
                          { key: "behavioral", label: "أنشطة سلوكية", emoji: "🤝", gradient: "from-blue-100 to-indigo-100", border: "border-blue-300", desc: "تعاون، مشاركة، تعديل سلوك" },
                          { key: "health", label: "أنشطة صحية وحركية", emoji: "🏃", gradient: "from-green-100 to-emerald-100", border: "border-green-300", desc: "تمارين، تحديات يومية" },
                          { key: "creative", label: "أنشطة إبداعية", emoji: "🎨", gradient: "from-pink-100 to-rose-100", border: "border-pink-300", desc: "رسم، قصص، تلوين" },
                          { key: "social", label: "أنشطة اجتماعية", emoji: "🫂", gradient: "from-teal-100 to-cyan-100", border: "border-teal-300", desc: "تواصل، تعاون، تعبير" },
                          { key: "ai", label: "تحليل ذكي", emoji: "🧠", gradient: "from-purple-100 to-violet-100", border: "border-purple-300", desc: "اقتراحات مخصصة" },
                        ].map((section, i) => (
                          <motion.div key={section.key} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.08 }}>
                            <Card
                              className={`cursor-pointer hover:shadow-xl transition-all border-2 ${section.border} hover:scale-[1.02]`}
                              onClick={() => setActivityView(section.key)}
                            >
                              <CardContent className={`p-5 bg-gradient-to-br ${section.gradient} rounded-lg`}>
                                <div className="text-center space-y-2">
                                  <div className="text-4xl">{section.emoji}</div>
                                  <h3 className="text-base font-bold text-foreground">{section.label}</h3>
                                  <p className="text-xs text-muted-foreground">{section.desc}</p>
                                </div>
                              </CardContent>
                            </Card>
                          </motion.div>
                        ))}
                      </div>
                    </TabsContent>

                    <TabsContent value="badges" className="mt-4">
                      <AchievementBadges
                        totalStars={activityStats.totalStars}
                        totalScore={activityStats.totalScore}
                        totalActivities={activityStats.totalActivities}
                      />
                    </TabsContent>

                    <TabsContent value="history" className="mt-4">
                      {childId && <ActivityHistory childId={childId} />}
                    </TabsContent>
                  </Tabs>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Assignment Submit Dialog */}
      <Dialog open={!!submittingAssignment} onOpenChange={(open) => { if (!open) { setSubmittingAssignment(null); setSubmitFile(null); setSubmitFilePreview(null); setSubmitTextAnswer(""); stopVoiceAnswer(); } }}>
        <DialogContent className="max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <span className="text-2xl">📒</span>
              تسليم: {submittingAssignment?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            {/* Camera/upload area */}
            <div className="relative">
              <input
                type="file"
                accept="image/*,.pdf"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
                id="student-submission-file"
              />
              <label
                htmlFor="student-submission-file"
                className={`block cursor-pointer rounded-2xl border-3 border-dashed transition-all ${
                  submitFilePreview
                    ? "border-primary bg-primary/5"
                    : "border-muted-foreground/30 hover:border-primary hover:bg-primary/5"
                } p-6`}
              >
                {submitFilePreview ? (
                  <div className="text-center space-y-3">
                    <img src={submitFilePreview} alt="معاينة" className="max-h-48 mx-auto rounded-xl shadow-md" />
                    <p className="text-sm text-primary font-medium">📷 اضغط لتغيير الصورة</p>
                  </div>
                ) : (
                  <div className="text-center space-y-3 py-4">
                    <div className="w-20 h-20 rounded-full bg-primary/10 mx-auto flex items-center justify-center">
                      <Camera className="w-10 h-10 text-primary" />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-foreground">📸 صوّر الكراسة</p>
                      <p className="text-sm text-muted-foreground mt-1">اضغط هنا لتصوير أو اختيار صورة</p>
                    </div>
                  </div>
                )}
              </label>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">إجابة نصية (اختياري)</Label>
              <Textarea
                value={submitTextAnswer}
                onChange={(e) => setSubmitTextAnswer(e.target.value)}
                placeholder="اكتب الإجابة هنا أو استخدم الميكروفون..."
                rows={2}
                className="rounded-xl"
              />
              <Button
                type="button"
                variant={isListening ? "destructive" : "outline"}
                size="sm"
                className={`w-full gap-2 rounded-full ${isListening ? "animate-pulse" : ""}`}
                onClick={() => isListening ? stopVoiceAnswer() : startVoiceAnswer()}
              >
                {isListening ? (
                  <>
                    <MicOff className="w-4 h-4" />
                    إيقاف التسجيل...
                  </>
                ) : (
                  <>
                    <Mic className="w-4 h-4" />
                    🎤 جاوب بالصوت
                  </>
                )}
              </Button>
            </div>

            <Button
              className="w-full h-12 text-base rounded-full bg-gradient-to-l from-primary to-primary/80 shadow-lg"
              onClick={handleSubmitAssignment}
              disabled={submitLoading || (!submitFile && !submitTextAnswer.trim())}
            >
              {submitLoading ? (
                <Loader2 className="w-5 h-5 ml-2 animate-spin" />
              ) : (
                <Upload className="w-5 h-5 ml-2" />
              )}
              تسليم الواجب 🚀
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Smart Notebook - AI Lesson Slides */}
      {showSmartNotebook && lessonExplanation && selectedLesson && (
        <div className="fixed inset-0 z-50 bg-background">
          <LessonSlidesViewer
            slides={(lessonExplanation.slides as any[]) || []}
            lessonTitle={selectedLesson.title}
            onClose={() => setShowSmartNotebook(false)}
            onGenerateImage={handleGenerateSlideImage}
            generatingImages={generatingImages}
          />
        </div>
      )}
    </div>
  );
};

export default StudentDashboard;
